/**
 * Simplified Security Implementation
 * 
 * This is a pared-down version of our security features that focuses on 
 * basic functionality without interfering with application rendering.
 */

import DOMPurify from 'dompurify';

/**
 * Sanitize HTML to prevent XSS attacks
 * @param html HTML string to sanitize
 * @returns Sanitized HTML
 */
export function sanitizeHTML(html: string): string {
  if (!html) return '';
  
  // Use DOMPurify to sanitize HTML content
  const sanitized = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    ALLOWED_TAGS: [
      'a', 'b', 'br', 'div', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'i', 'li', 'ol', 'p', 'span', 'strong', 'u', 'ul'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
  });
  
  return sanitized;
}

/**
 * Sanitize text input to prevent XSS attacks
 * @param input User input to sanitize
 * @returns Sanitized input
 */
export function sanitizeInput(input: string): string {
  if (!input) return '';
  
  // Basic sanitization
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Generate a simple CSRF token
 * @returns CSRF token
 */
export function generateCSRFToken(): string {
  // Create a random string for CSRF token
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
}

/**
 * Basic security headers object
 * @returns Object with security headers
 */
export function getSecurityHeaders(): Record<string, string> {
  return {
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'SAMEORIGIN', // Less strict than DENY
    'X-XSS-Protection': '1; mode=block',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
  };
}

/**
 * Initialize basic security features
 * This is a simplified version that won't interfere with app rendering
 */
export function initializeBasicSecurity(): void {
  console.log('Basic security features initialized');
}
